import React from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  ReferenceField,
  ReferenceManyField,
  Show,
  TextField,
  useRecordContext
} from 'react-admin';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { ContactNameLink } from './ContactNameLink.tsx';
import { DealNameLink } from './DealNameLink.tsx';
import { GroupNameLink } from './GroupNameLink.tsx';
import { RELATED_PERSON_CHOICES } from './constants/relatedPeople.ts';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';
import { ShareContactButton } from './components/ShareContactButton.tsx';
import { ContactMailSection } from './components/ContactMailSection.tsx';
import { ContactRelationshipSection } from './components/ContactRelationshipSection.tsx';
import { RenewalsSection } from '../renewals/RenewalsSection.tsx';
import { ConditionalFollowUpDateField } from '../components/ConditionalFollowUpDateField.tsx';
import { ActivityModal } from '../components/ActivityModal.tsx';
import {
  DetailRow,
  EmptyState,
  RecordEditButton,
  RecordHeader,
  RecordPage,
  RecordSection,
  RecordTabs
} from '../components/record/RecordPage.tsx';

type PhoneEntry = { phone: string; type?: string; extension?: string };

type ContactRecord = {
  id?: number;
  firstName?: string;
  lastName?: string;
  title?: string;
  primaryStreet?: string;
  primaryCity?: string;
  primaryState?: string;
  primaryZip?: string;
  websiteUrl?: string;
  linkedInUrl?: string;
  notes?: string;
  referredById?: number | null;
  referredByGroupId?: number | null;
  emails?: { type?: string; email?: string }[];
  phones?: { type?: string; phone?: string; extension?: string }[];
  relatedPeople?: {
    relationship?: string;
    name?: string;
    primaryEmail?: string;
    primaryPhone?: string;
    notes?: string;
  }[];
};

const phoneLabel = (phone: PhoneEntry) => {
  const extension = phone.extension ? ` x${phone.extension}` : '';
  return phone.type ? `${phone.type}: ${phone.phone}${extension}` : `${phone.phone}${extension}`;
};

const formatTypeLabel = (value: string | undefined, fallback: string) => {
  const normalized = value?.trim().toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : fallback;
};

const QuickActions = ({
  allPhones,
  textablePhones,
  primaryEmail,
  linkedInUrl,
  websiteUrl
}: {
  allPhones: PhoneEntry[];
  textablePhones: PhoneEntry[];
  primaryEmail?: string;
  linkedInUrl?: string;
  websiteUrl?: string;
}) => {
  const [callAnchor, setCallAnchor] = React.useState<null | HTMLElement>(null);
  const [textAnchor, setTextAnchor] = React.useState<null | HTMLElement>(null);

  if (!allPhones.length && !primaryEmail && !linkedInUrl && !websiteUrl) return null;

  const actionChipProps = { clickable: true, variant: 'outlined' as const, size: 'small' as const };

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
      {allPhones.length === 1 ? (
        <Chip
          {...actionChipProps}
          icon={<PhoneOutlinedIcon />}
          label="Call"
          component="a"
          href={`tel:${allPhones[0].phone}`}
        />
      ) : null}
      {allPhones.length > 1 ? (
        <>
          <Chip
            {...actionChipProps}
            icon={<PhoneOutlinedIcon />}
            label="Call"
            onClick={(event) => setCallAnchor(event.currentTarget)}
          />
          <Menu
            anchorEl={callAnchor}
            open={Boolean(callAnchor)}
            onClose={() => setCallAnchor(null)}
          >
            {allPhones.map((phone, index) => (
              <MenuItem
                key={`${phone.phone}-${index}`}
                component="a"
                href={`tel:${phone.phone}`}
                onClick={() => setCallAnchor(null)}
              >
                <ListItemIcon>
                  <PhoneOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{phoneLabel(phone)}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
      {textablePhones.length === 1 ? (
        <Chip
          {...actionChipProps}
          icon={<SmsOutlinedIcon />}
          label="Text"
          component="a"
          href={`sms:${textablePhones[0].phone}`}
        />
      ) : null}
      {textablePhones.length > 1 ? (
        <>
          <Chip
            {...actionChipProps}
            icon={<SmsOutlinedIcon />}
            label="Text"
            onClick={(event) => setTextAnchor(event.currentTarget)}
          />
          <Menu
            anchorEl={textAnchor}
            open={Boolean(textAnchor)}
            onClose={() => setTextAnchor(null)}
          >
            {textablePhones.map((phone, index) => (
              <MenuItem
                key={`${phone.phone}-${index}`}
                component="a"
                href={`sms:${phone.phone}`}
                onClick={() => setTextAnchor(null)}
              >
                <ListItemIcon>
                  <SmsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{phoneLabel(phone)}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
      {primaryEmail ? (
        <Chip
          {...actionChipProps}
          icon={<EmailOutlinedIcon />}
          label="Email"
          component="a"
          href={`mailto:${primaryEmail}`}
        />
      ) : null}
      {linkedInUrl ? (
        <Chip
          {...actionChipProps}
          icon={<LinkedInIcon />}
          label="LinkedIn"
          component="a"
          href={linkedInUrl.startsWith('http') ? linkedInUrl : `https://${linkedInUrl}`}
          target="_blank"
          rel="noopener noreferrer"
        />
      ) : null}
      {websiteUrl ? (
        <Chip
          {...actionChipProps}
          icon={<LanguageOutlinedIcon />}
          label="Website"
          component="a"
          href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
          target="_blank"
          rel="noopener noreferrer"
        />
      ) : null}
    </Stack>
  );
};

const relatedPersonLabels = new Map(
  RELATED_PERSON_CHOICES.map((choice) => [choice.id, choice.name])
);

const ContactOverview = ({ record }: { record: ContactRecord }) => {
  const addressParts = [
    record.primaryStreet,
    record.primaryCity,
    record.primaryState,
    record.primaryZip
  ]
    .map((part) => part?.trim())
    .filter(Boolean);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 7 }}>
        <RecordSection
          title="Contact information"
          action={<RecordEditButton label="Edit" variant="text" />}
          sx={{ height: '100%' }}
        >
          <Stack spacing={0.25} divider={<Divider />}>
            {record.emails?.map((item, index) => {
              const email = item.email?.trim();
              if (!email) return null;
              return (
                <DetailRow
                  key={`${email}-${index}`}
                  icon={<EmailOutlinedIcon fontSize="small" />}
                  label={formatTypeLabel(item.type, 'Email')}
                >
                  <MuiLink href={`mailto:${email}`} sx={{ wordBreak: 'break-all' }}>
                    {email}
                  </MuiLink>
                </DetailRow>
              );
            })}
            {record.phones?.map((item, index) => {
              const phone = item.phone?.trim();
              if (!phone) return null;
              const display = `${phone}${item.extension?.trim() ? ` x${item.extension.trim()}` : ''}`;
              return (
                <DetailRow
                  key={`${phone}-${index}`}
                  icon={<PhoneOutlinedIcon fontSize="small" />}
                  label={formatTypeLabel(item.type, 'Phone')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MuiLink href={`tel:${phone}`}>{display}</MuiLink>
                    <IconButton
                      component="a"
                      href={`sms:${phone}`}
                      size="small"
                      aria-label={`Text ${phone}`}
                    >
                      <SmsOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                </DetailRow>
              );
            })}
            {record.websiteUrl?.trim() ? (
              <DetailRow icon={<LanguageOutlinedIcon fontSize="small" />} label="Website">
                <MuiLink
                  href={
                    record.websiteUrl.startsWith('http')
                      ? record.websiteUrl
                      : `https://${record.websiteUrl}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {record.websiteUrl}
                </MuiLink>
              </DetailRow>
            ) : null}
            {record.linkedInUrl?.trim() ? (
              <DetailRow icon={<LinkedInIcon fontSize="small" />} label="LinkedIn">
                <MuiLink
                  href={
                    record.linkedInUrl.startsWith('http')
                      ? record.linkedInUrl
                      : `https://${record.linkedInUrl}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {record.linkedInUrl}
                </MuiLink>
              </DetailRow>
            ) : null}
            {!record.emails?.some((item) => item.email?.trim()) &&
            !record.phones?.some((item) => item.phone?.trim()) &&
            !record.websiteUrl?.trim() &&
            !record.linkedInUrl?.trim() ? (
              <EmptyState>No contact methods on file.</EmptyState>
            ) : null}
          </Stack>
        </RecordSection>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <RecordSection
          title="Details"
          action={<RecordEditButton label="Edit" variant="text" />}
          sx={{ height: '100%' }}
        >
          <Stack spacing={0.25} divider={<Divider />}>
            <DetailRow icon={<BusinessOutlinedIcon fontSize="small" />} label="Company">
              <ReferenceField
                source="companyId"
                reference="companies"
                link="show"
                emptyText="Not set"
              >
                <TextField source="name" />
              </ReferenceField>
            </DetailRow>
            <DetailRow label="Industry">
              <ReferenceField
                source="industryId"
                reference="industry"
                link={false}
                emptyText="Not set"
              >
                <TextField source="name" />
              </ReferenceField>
            </DetailRow>
            <DetailRow label="Status">
              <ReferenceField
                source="contactStatusId"
                reference="contact-statuses"
                link={false}
                emptyText="Not set"
              >
                <TextField source="name" />
              </ReferenceField>
            </DetailRow>
            <DetailRow label="Contact ID">
              <Typography variant="body2">{record.id}</Typography>
            </DetailRow>
          </Stack>
        </RecordSection>
      </Grid>

      <Grid size={{ xs: 12, md: 7 }}>
        <RecordSection
          title="General notes"
          action={<RecordEditButton label="Edit" variant="text" />}
          sx={{ height: '100%' }}
        >
          {record.notes?.trim() ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <NotesOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.notes.trim()}
              </Typography>
            </Box>
          ) : (
            <EmptyState>No general notes on file.</EmptyState>
          )}
        </RecordSection>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <RecordSection
          title="Address"
          action={<RecordEditButton label="Edit" variant="text" />}
          sx={{ height: '100%' }}
        >
          {addressParts.length ? (
            <DetailRow icon={<LocationOnOutlinedIcon fontSize="small" />} label="Primary">
              <Typography variant="body2">
                {record.primaryStreet?.trim() ? (
                  <>
                    {record.primaryStreet.trim()}
                    <br />
                  </>
                ) : null}
                {[record.primaryCity?.trim(), record.primaryState?.trim()]
                  .filter(Boolean)
                  .join(', ')}
                {record.primaryZip?.trim() ? ` ${record.primaryZip.trim()}` : ''}
              </Typography>
            </DetailRow>
          ) : (
            <EmptyState>No address on file.</EmptyState>
          )}
        </RecordSection>
      </Grid>

      {record.id ? (
        <Grid size={{ xs: 12 }}>
          <RenewalsSection
            filter={{ contactId: record.id }}
            addTo={{ contactId: record.id }}
            description="When each line comes up. The account can only move around its renewal date."
          />
        </Grid>
      ) : null}

      {record.id ? (
        <Grid size={{ xs: 12 }}>
          <ContactRelationshipSection
            contactId={record.id}
            hasEmail={Boolean(record.emails?.some((item) => item.email?.trim()))}
          />
        </Grid>
      ) : null}

      {record.id ? (
        <Grid size={{ xs: 12 }}>
          <ContactMailSection contactId={record.id} />
        </Grid>
      ) : null}
    </Grid>
  );
};

const ContactActivity = () => (
  <RecordSection
    title="Activity"
    description="A chronological view of outreach and follow-up."
    action={<RecordEditButton label="Manage" variant="outlined" returnTab="activity" />}
  >
    <ReferenceManyField reference="contact-activities" target="contactId">
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        empty={<EmptyState>No activity yet.</EmptyState>}
      >
        <TextField source="type" />
        <TextField source="note" />
        <DateField
          source="dateSent"
          label="Activity date"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
        <ConditionalFollowUpDateField
          source="followUpDate"
          label="Follow up"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
      </Datagrid>
    </ReferenceManyField>
  </RecordSection>
);

const ContactRelationships = ({ record }: { record: ContactRecord }) => (
  <Stack spacing={2}>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <RecordSection
          title="Referral source"
          action={<RecordEditButton label="Edit" variant="text" returnTab="relationships" />}
        >
          <Stack spacing={0.25} divider={<Divider />}>
            <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label="Referred by contact">
              {record.referredById ? (
                <ReferenceField source="referredById" reference="contacts" link="show">
                  <FunctionField
                    render={(contact: { firstName?: string; lastName?: string }) =>
                      `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || 'Not set'
                    }
                  />
                </ReferenceField>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </DetailRow>
            <DetailRow label="Referred by group">
              {record.referredByGroupId ? (
                <ReferenceField source="referredByGroupId" reference="groups" link="show">
                  <TextField source="name" />
                </ReferenceField>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </DetailRow>
          </Stack>
        </RecordSection>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <RecordSection
          title="Related people"
          action={
            <RecordEditButton
              editPath="related-people"
              label="Manage"
              variant="text"
              returnTab="relationships"
            />
          }
        >
          {record.relatedPeople?.length ? (
            <Stack spacing={1}>
              {record.relatedPeople.map((person, index) => {
                const relationship = person.relationship?.trim();
                return (
                  <Paper
                    key={`${person.name ?? 'person'}-${index}`}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <PersonOutlineIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">
                        {person.name?.trim() || 'Unnamed person'}
                      </Typography>
                      {relationship ? (
                        <Chip
                          label={relatedPersonLabels.get(relationship) ?? relationship}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                    </Box>
                    <Stack spacing={0.35} sx={{ mt: 1, pl: 3.5 }}>
                      {person.primaryEmail?.trim() ? (
                        <MuiLink href={`mailto:${person.primaryEmail}`} variant="body2">
                          {person.primaryEmail}
                        </MuiLink>
                      ) : null}
                      {person.primaryPhone?.trim() ? (
                        <MuiLink href={`tel:${person.primaryPhone}`} variant="body2">
                          {person.primaryPhone}
                        </MuiLink>
                      ) : null}
                      {person.notes?.trim() ? (
                        <Typography variant="body2" color="text.secondary">
                          {person.notes.trim()}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <EmptyState>No related people yet.</EmptyState>
          )}
        </RecordSection>
      </Grid>
    </Grid>

    <RecordSection
      title="Introductions made"
      action={<RecordEditButton label="Manage" variant="text" returnTab="relationships" />}
    >
      <ReferenceManyField reference="introduced-by" target="id">
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          empty={<EmptyState>No introductions yet.</EmptyState>}
        >
          <FunctionField label="Name" render={() => <ContactNameLink />} />
          <DateField source="dateIntroduced" label="Date introduced" />
        </Datagrid>
      </ReferenceManyField>
    </RecordSection>

    <RecordSection
      title="Referrals made"
      action={<RecordEditButton label="Manage" variant="text" returnTab="relationships" />}
    >
      <ReferenceManyField reference="referredTo" target="id">
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          empty={<EmptyState>No referrals yet.</EmptyState>}
        >
          <FunctionField label="Name" render={() => <ContactNameLink />} />
          <DateField source="dateReferred" label="Date referred" />
        </Datagrid>
      </ReferenceManyField>
    </RecordSection>

    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 4 }}>
        <RecordSection
          title="Opportunities"
          action={<RecordEditButton label="Manage" variant="text" returnTab="relationships" />}
        >
          <ReferenceManyField reference="opportunities" target="contactId">
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              empty={<EmptyState>No opportunities yet.</EmptyState>}
            >
              <FunctionField label="Name" render={() => <DealNameLink />} />
              <TextField source="status" />
              <TextField source="opportunityStage.name" label="Stage" />
            </Datagrid>
          </ReferenceManyField>
        </RecordSection>
      </Grid>
      <Grid size={{ xs: 12, lg: 4 }}>
        <RecordSection
          title="Groups"
          action={<RecordEditButton label="Manage" variant="text" returnTab="relationships" />}
        >
          <ReferenceManyField reference="group-contacts" target="contactId">
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              empty={<EmptyState>Not in any groups yet.</EmptyState>}
            >
              <FunctionField label="Group" render={() => <GroupNameLink />} />
            </Datagrid>
          </ReferenceManyField>
        </RecordSection>
      </Grid>
      <Grid size={{ xs: 12, lg: 4 }}>
        <RecordSection
          title="Projects"
          action={<RecordEditButton label="Manage" variant="text" returnTab="relationships" />}
        >
          <ReferenceManyField reference="project-contacts" target="contactId">
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              empty={<EmptyState>Not in any projects yet.</EmptyState>}
            >
              <FunctionField
                label="Project"
                render={(projectContact: { projectId?: number; project?: { name?: string } }) =>
                  projectContact?.projectId && projectContact.project?.name ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/projects/${projectContact.projectId}/show`}
                    >
                      {projectContact.project.name}
                    </MuiLink>
                  ) : null
                }
              />
              <TextField source="stage" label="Stage" />
            </Datagrid>
          </ReferenceManyField>
        </RecordSection>
      </Grid>
    </Grid>
  </Stack>
);

// The weekly digest links here with ?followUp=1. Open the activity modal once,
// then drop the param so a refresh does not reopen it.
const DigestFollowUp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!searchParams.has('followUp')) return;
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('followUp');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <ActivityModal
      open={open}
      setOpen={setOpen}
      keyName="contactId"
      resource="contact-activities"
    />
  );
};

const ContactShowContent = () => {
  const record = useRecordContext<ContactRecord>();
  if (!record) return null;

  const fullName = `${record.firstName ?? ''} ${record.lastName ?? ''}`.trim() || 'Contact';
  const initials =
    `${record.firstName?.[0] ?? ''}${record.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const allPhones: PhoneEntry[] = (record.phones ?? [])
    .map((phone) => ({
      phone: phone.phone?.trim() ?? '',
      type: phone.type?.trim(),
      extension: phone.extension?.trim()
    }))
    .filter((phone) => Boolean(phone.phone));
  const mobilePhones = allPhones.filter(
    (phone) => phone.type && ['CELL', 'MOBILE'].includes(phone.type.toUpperCase())
  );
  const textablePhones = mobilePhones.length ? mobilePhones : allPhones;

  return (
    <RecordPage>
      <RecordHeader
        avatar={
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontWeight: 700 }}>
            {initials}
          </Avatar>
        }
        title={fullName}
        subtitle={record.title}
        actions={
          <>
            <ShareContactButton />
            <RecordEditButton />
          </>
        }
        quickActions={
          <QuickActions
            allPhones={allPhones}
            textablePhones={textablePhones}
            primaryEmail={record.emails?.find((item) => item.email?.trim())?.email?.trim()}
            linkedInUrl={record.linkedInUrl}
            websiteUrl={record.websiteUrl}
          />
        }
      />
      <RecordTabs
        tabs={[
          { value: 'overview', label: 'Overview', content: <ContactOverview record={record} /> },
          { value: 'activity', label: 'Activity', content: <ContactActivity /> },
          {
            value: 'relationships',
            label: 'Relationships',
            content: <ContactRelationships record={record} />
          },
          {
            value: 'notes',
            label: 'Notes',
            content: (
              <RecordSection
                title="Notes"
                action={
                  <RecordEditButton
                    editPath="notes"
                    label="Manage"
                    variant="outlined"
                    returnTab="notes"
                  />
                }
              >
                <NoteListSection entityType="CONTACT" readOnly hideTitle />
              </RecordSection>
            )
          },
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <RecordSection
                title="Documents"
                action={
                  <RecordEditButton
                    editPath="documents"
                    label="Manage"
                    variant="outlined"
                    returnTab="documents"
                  />
                }
              >
                <DocumentListSection entityType="CONTACT" readOnly hideTitle />
              </RecordSection>
            )
          }
        ]}
      />
      <DigestFollowUp />
    </RecordPage>
  );
};

const ContactShow = () => (
  <Container maxWidth="lg">
    <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
        Dashboard
      </MuiLink>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/contacts">
        Contacts
      </MuiLink>
      <Typography color="text.primary">View</Typography>
    </Breadcrumbs>
    <Show actions={false} component="div" sx={{ '& .RaShow-noActions': { mt: 0 } }}>
      <ContactShowContent />
    </Show>
  </Container>
);

export default ContactShow;
